import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { formatInteger } from '../../../../utils/formatting'
import { CONTACTS_TABLE_KEY } from 'constants/tableQueryKeys'
import { Tabs } from 'components/Tabs'
import { dataTableSortingStoreRepo } from 'components/DataTable/DataTableSorting/DataTableSortingStore'
import {
  filterStoreRepo,
  useFilterParams,
} from 'components/Filters/FilterStore'
import useApiService from 'services/api'
import { MobileListViewHeader } from 'components/MobileListViewHeader'
import { ContactResponse } from 'models/contacts'
import { MobileContactList } from './MobileContactList'
import { MobileContactRequestList } from './MobileContactRequestList'
import { SortingOptionLabel } from 'components/SortSelect/SortingOptionLabel'
import MobileEditContactModal from 'components/Modals/MobileEditContactModal'
import { FilterChips } from 'components/Filters/components/FilterChips/FilterChips'
import {
  BackButton,
  PageContainer,
} from 'components/MobileSearch/MobileSearch.styles'
import { ChevronLeft } from 'lucide-react'

const sortingOptions = [
  {
    label: <SortingOptionLabel label="Last Updated" type="number" />,
    value: 'modified',
  },
  {
    label: <SortingOptionLabel label="Last Updated" type="number" desc />,
    value: '-modified',
  },
  {
    label: <SortingOptionLabel label="Date Added" type="number" />,
    value: 'created',
  },
  {
    label: <SortingOptionLabel label="Date Added" type="number" desc />,
    value: '-created',
  },
  { label: <SortingOptionLabel label="Name" />, value: 'name' },
  {
    label: <SortingOptionLabel label="Name" desc />,
    value: '-name',
  },
]

export function MobileContactsPage() {
  const navigate = useNavigate()
  const [selectedTab, setSelectedTab] = useState<string>('all_contacts')
  const [showEditContactModal, setShowEditContactModal] =
    useState<ContactResponse | null>(null)
  const [searchParams] = useSearchParams()
  const api = useApiService()

  const IDENTIFIER = 'CONTACTS_TABLE'

  // Read chain_id from URL query params
  const filterIdFromUrl = searchParams.get('contact_company_id')

  const { data: contactsCounts } = api.useGetContactsCounts(
    filterIdFromUrl ? { contact_company_id: filterIdFromUrl } : {}
  )
  const sortingStore = dataTableSortingStoreRepo.getStore(CONTACTS_TABLE_KEY)()

  const contactsFilterStore = filterStoreRepo.getStore(IDENTIFIER)
  const filterStore = contactsFilterStore()
  const { page, setPage } = filterStore

  const { sorting } = sortingStore

  const filtersAsParams = {
    ...useFilterParams(contactsFilterStore),
  }

  const filterParams = useMemo(() => {
    const params: Record<string, unknown> = { ...filtersAsParams }
    return params
  }, [filtersAsParams])

  const sortParams = useMemo(() => {
    const params: Record<string, unknown> = {}

    if (sorting?.length) {
      params['sort'] = sorting[0].desc ? '-' + sorting[0]?.id : sorting[0]?.id
    }

    return params
  }, [sorting])

  useEffect(() => {
    if (page > 1) {
      setPage(1)
    }
  }, [selectedTab])

  const filterAndSortParams = useMemo(
    () => ({
      ...filterParams,
      ...sortParams,
      ...(filterIdFromUrl ? { company: filterIdFromUrl } : {}),
    }),
    [filterParams, sortParams, selectedTab, filterIdFromUrl]
  )

  const handleEditContact = (contact: ContactResponse) => {
    setShowEditContactModal(contact)
  }

  return (
    <PageContainer>
      <BackButton onClick={() => navigate(-1)}>
        <ChevronLeft size={20} />
        <span>Back</span>
      </BackButton>

      <div className="w-full mt-3 ml-2">
        <Tabs
          tabs={[
            {
              key: 'all_contacts',
              title: `All Contacts (${formatInteger(contactsCounts?.all_contacts_count)})`,
            },
            {
              key: 'contact_requests',
              title: `Requests (${formatInteger(contactsCounts?.requests_count)})`,
            },
          ]}
          selectedTab={selectedTab}
          setSelectedTab={setSelectedTab}
        />
      </div>

      <MobileListViewHeader
        sortingOptions={sortingOptions}
        filterStoreIdentifier={IDENTIFIER}
        tableKey={CONTACTS_TABLE_KEY}
        defaultSorting={[{ id: 'modified', desc: true }]}
      />

      <FilterChips
        store={contactsFilterStore}
        identifier={IDENTIFIER}
        clearAllButton
        showActive
      />

      {selectedTab === 'all_contacts' ? (
        <MobileContactList
          page={page}
          filterAndSortParams={filterAndSortParams}
          onEdit={handleEditContact}
        />
      ) : (
        <MobileContactRequestList
          page={page}
          filterAndSortParams={filterAndSortParams}
        />
      )}

      {showEditContactModal && (
        <MobileEditContactModal
          show={!!showEditContactModal}
          handleClose={() => setShowEditContactModal(null)}
          contact={showEditContactModal}
        />
      )}
    </PageContainer>
  )
}
